import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const scenarioData: Record<string, { title: string; description: string; content: React.ReactNode }> = {
  scan: {
    title: 'Scan a Product Experience',
    description: 'This is what your customers see when they scan the QR code on your product. A branded, mobile-first experience with product details, support, and registration — no app required.',
    content: (
      <div className="mt-8 rounded-xl overflow-hidden border border-gray-200 bg-white" style={{ maxWidth: 420, margin: '0 auto' }}>
        <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 text-xs text-gray-400">
          <div className="flex gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-red-300" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-300" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-300" />
          </div>
          <span className="flex-1 text-center">brandedmark.com/scan/demo-product</span>
        </div>
        <iframe
          src="https://monverta.brandedmark.com/scan"
          title="Product scan demo"
          className="w-full border-0"
          style={{ height: 600 }}
          loading="lazy"
          sandbox="allow-scripts allow-same-origin"
          onError={() => {}}
        />
      </div>
    ),
  },
  register: {
    title: 'Register Ownership',
    description: 'Passkey-powered product ownership in under 30 seconds. No passwords, no app downloads — just tap, verify, and own.',
    content: (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Scan', desc: 'Customer scans the QR code on their new product — on the product itself or inside the packaging.' },
              { step: '2', title: 'Verify', desc: 'A passkey prompt appears. One tap on their phone authenticates them — no password, no app download, no account creation.' },
              { step: '3', title: 'Own', desc: 'Product is registered to them. Warranty activates. They get instant access to setup guides, support, and spare parts.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold" style={{ background: '#00B37E' }}>{s.step}</div>
                <h3 className="font-semibold text-gray-900 mb-1">{s.title}</h3>
                <p className="text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Why passkeys?</h2>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2"><span className="text-[#00B37E] font-bold mt-0.5">+</span> No password database to breach — credentials live on the customer's device</li>
            <li className="flex items-start gap-2"><span className="text-[#00B37E] font-bold mt-0.5">+</span> Phishing-resistant — FIDO2/WebAuthn standard used by Google, Apple, Microsoft</li>
            <li className="flex items-start gap-2"><span className="text-[#00B37E] font-bold mt-0.5">+</span> Works across devices — biometric or PIN, no app needed</li>
            <li className="flex items-start gap-2"><span className="text-[#00B37E] font-bold mt-0.5">+</span> Ownership transfer built in — resale, gifting, fleet handover</li>
          </ul>
        </div>
        <p className="text-center text-sm text-gray-400">Interactive demo coming soon — join the waitlist to be notified.</p>
      </div>
    ),
  },
  warranty: {
    title: 'Warranty & Returns',
    description: 'Replace PDF warranty cards and email-based returns with a 3-tap digital flow. Customers register at unboxing, claim in seconds.',
    content: (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">The 3-tap warranty flow</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Scan product', desc: 'Customer scans the QR at unboxing. Product and serial number are pre-filled — no typing.' },
              { step: '2', title: 'Confirm details', desc: 'Name and email captured. Proof of purchase optional — the scan event IS the proof.' },
              { step: '3', title: 'Warranty active', desc: 'Instant confirmation. Warranty period starts. Customer gets support access and spare parts in one place.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold" style={{ background: '#00B37E' }}>{s.step}</div>
                <h3 className="font-semibold text-gray-900 mb-1">{s.title}</h3>
                <p className="text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">The numbers</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            {[
              { stat: '35%', label: 'Registration rate (vs 10% paper)' },
              { stat: '30s', label: 'Time to register' },
              { stat: '0', label: 'App downloads required' },
              { stat: '80%', label: 'Support queries resolved by scan' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-2xl font-bold" style={{ color: '#00B37E' }}>{s.stat}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-center text-sm text-gray-400">Interactive demo coming soon — join the waitlist to be notified.</p>
      </div>
    ),
  },
  dashboard: {
    title: 'Brand Dashboard',
    description: 'See every product, every scan, every registration. Real-time analytics, QR code management, and customer insights in one place.',
    content: (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">What you see in the dashboard</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: 'Product Library', desc: 'Every product line, SKU, and individual serial number. Upload once, manage forever.' },
              { title: 'Scan Analytics', desc: 'Where, when, and how customers interact with your products. Heatmaps, trends, and engagement metrics.' },
              { title: 'Registration Pipeline', desc: 'Live registration feed. See who owns what, warranty status, and registration rates by product line.' },
              { title: 'QR Code Manager', desc: 'Generate, download, and track QR codes. GS1 Digital Link format. Batch operations for production runs.' },
              { title: 'Support Insights', desc: 'What customers ask about. Top issues, resolution rates, and content gaps you can fill.' },
              { title: 'DPP Status', desc: 'Compliance readiness per product. Data completeness scores. Export-ready for EU audits.' },
            ].map((item) => (
              <div key={item.title} className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-center text-sm text-gray-400">Interactive dashboard demo coming soon — join the waitlist to be notified.</p>
      </div>
    ),
  },
  dpp: {
    title: 'DPP Compliance',
    description: 'A live EU Digital Product Passport — materials, recyclability, carbon footprint, and repair information in a machine-readable, regulation-ready format.',
    content: (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">What a Digital Product Passport contains</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-semibold text-gray-900">Data Category</th>
                  <th className="text-left py-2 font-semibold text-gray-900">Examples</th>
                </tr>
              </thead>
              <tbody className="text-gray-600">
                {[
                  ['Product Identity', 'GTIN, serial number, batch, manufacturer, model'],
                  ['Materials & Composition', 'Bill of materials, hazardous substances, recycled content %'],
                  ['Carbon Footprint', 'Lifecycle CO2e, manufacturing emissions, transport'],
                  ['Durability & Repairability', 'Expected lifespan, repair score, spare parts availability'],
                  ['End of Life', 'Disassembly instructions, recyclability %, collection points'],
                  ['Compliance', 'ESPR category, conformity declaration, test reports'],
                ].map(([cat, examples]) => (
                  <tr key={cat} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium text-gray-900">{cat}</td>
                    <td className="py-2">{examples}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">EU DPP Timeline</h2>
          <div className="space-y-3">
            {[
              { date: 'May 2026', desc: 'Batteries >2kWh — QR code with capacity, durability, recyclability', active: true },
              { date: '2027', desc: 'Textiles — full material disclosure, carbon footprint, repairability score', active: false },
              { date: '2028', desc: 'Electronics — hazardous substances, recycled content, expected lifespan', active: false },
              { date: '2030', desc: 'All products under ESPR — universal Digital Product Passport requirement', active: false },
            ].map((item) => (
              <div key={item.date} className={'flex items-start gap-3 p-3 rounded-lg ' + (item.active ? 'bg-green-50 border border-green-200' : 'bg-gray-50')}>
                <span className={'text-sm font-bold whitespace-nowrap ' + (item.active ? 'text-[#00B37E]' : 'text-gray-400')}>{item.date}</span>
                <span className="text-sm text-gray-600">{item.desc}</span>
                {item.active && <span className="text-xs font-semibold text-[#00B37E] whitespace-nowrap">IMMINENT</span>}
              </div>
            ))}
          </div>
        </div>
        <p className="text-center text-sm text-gray-400">Live DPP demo with Monverta sample products coming soon.</p>
      </div>
    ),
  },
};

export const DemoScenario: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem('bm_demo_email')) {
      navigate('/see-a-demo');
    }
  }, [navigate]);

  const scenario = slug ? scenarioData[slug] : null;

  if (!scenario) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">Demo not found</h1>
          <Link to="/see-a-demo/library" className="text-[#00B37E] hover:underline">Back to library</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/see-a-demo/library" className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back to library
            </Link>
            <img src="/img/logo-full.png" alt="BrandedMark" className="h-6" onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }} />
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4" style={{ color: '#241E61' }}>
            {scenario.title}
          </h1>
          <p className="text-lg text-gray-500 mb-8 max-w-2xl">
            {scenario.description}
          </p>

          {scenario.content ? (
            scenario.content
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Coming soon</h2>
              <p className="text-gray-500 mb-8">This interactive demo is currently in production. Check back soon.</p>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 bg-white rounded-xl border border-gray-200 p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to get started?</h3>
            <p className="text-gray-500 mb-6">Join the waitlist and be first to launch your connected product experience.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white text-base transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(90deg, #00B37E 0%, #00D084 100%)' }}
            >
              Join the waitlist
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
